import React, { useState, useEffect } from 'react';
import { TextField, IconButton } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import { Autocomplete } from '@material-ui/lab';
import { API } from 'aws-amplify';
import { getAPIName } from '../../config';

export interface Option_Properties {
    options_path?: string;
    key_selector?: string;
    additional_selector_value?: string;
    value_selector?: string;
    label?: string;
    // allow passing a preselected option value (the id) so the autocomplete can show it
    value?: any;
    onChange?: (value: any) => void;
    onInitialLoad?: (value: any) => void; // Called when initial value is loaded from options
    addOption?: {
      label: string;
      value?: string;
    };
    addModal?: (props: {
      open: boolean;
      onClose: () => void;
      onSuccess: () => void;
    }) => JSX.Element;
    editModal?: (props: {
      open: boolean;
      onClose: () => void;
      onSuccess: () => void;
      option: Option;
    }) => JSX.Element;
    options?: Option[];
    refreshTrigger?: any;
  };
  interface Option {
    label: string;
    value: any;
    data: any;
  }

export default function DropdownWithSearch(props: Option_Properties) {
    const [options, setOptions] = useState<Option[]>([]);
    const [selectedOption, setSelectedOption] = useState<Option | null>(null);
    const [open, setOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [editingOption, setEditingOption] = useState<Option | null>(null);
    const addOptionValue = props.addOption?.value || '__add__';
    const {
      options_path,
      key_selector = 'id',
      value_selector = 'name',
      additional_selector_value = 'description',
      label = 'Select an option',
      value,
      onChange,
      onInitialLoad
    } = props;
  
    const fetchOptions = async () => {
        if (props.options) return setOptions(props.options);
        if (!options_path) return;
        try {
          const response: any = await API.get(getAPIName(), options_path, {});
          const items = response.body;
          const transformed = (items.map((item: any) => ({
            label: item[additional_selector_value] || item[value_selector],
            value: item[key_selector],
            data: item
          })) as Option[])
            .sort((a, b) => a.label.localeCompare(b.label));
          setOptions(transformed);
          const match = transformed.find(o => o.value === selectedOption?.value);
          if (match) {
              setSelectedOption(match);
              onChange?.(match);
          }
        } catch (e) {
          console.error('Failed to load options:', e);
        }
      }
  

    useEffect(() => {
      fetchOptions();
    }, [options_path, key_selector, value_selector, additional_selector_value, props.refreshTrigger]);

    // Keep selectedOption in sync with `value` prop (including clearing)
    useEffect(() => {
      if (value === undefined || value === null) {
        setSelectedOption(null);
        return;
      }
      if (options.length === 0) return;
      const match = options.find((o) => o.value === value);
      if (match) {
        setSelectedOption(match);
        // Trigger onInitialLoad callback with the matched option data
        if (onInitialLoad && selectedOption?.value !== match.value) {
          onInitialLoad(match);
        }
      }
    }, [value, options]);

    const handleEditClick = (e: React.MouseEvent, option: Option) => {
      e.stopPropagation();
      e.preventDefault();
      setEditingOption(option);
      setEditOpen(true);
    };

    const allOptions = props.addOption ? [{ label: props.addOption.label, value: addOptionValue, data: null }, ...options] : options;

    return (
      <>
        <Autocomplete
          options={allOptions}
          value={selectedOption}
          getOptionLabel={(option) => option.label}
          onChange={(_, val) => {
            if (val?.value === addOptionValue) return setOpen(true);
            setSelectedOption(val || null);
            onChange?.(val);
          }}
          renderOption={(option) =>
            option.value === addOptionValue ? (
              <span style={{ color: '#1976d2', fontWeight: 500 }}>{option.label}</span>
            ) : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{option.label}</span>
                {props.editModal && (
                  <IconButton
                    size="small"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => handleEditClick(e, option)}
                    aria-label="Edit authority"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                )}
              </div>
            )
          }
          renderInput={(params) => (
            <TextField {...params} variant="standard" placeholder={label} />
          )}
          fullWidth
        />
        {props.addModal?.({
          open,
          onClose: () => setOpen(false),
          onSuccess: () => {
            setOpen(false);
            fetchOptions();
          }
        })}
        {editingOption && props.editModal?.({
          open: editOpen,
          onClose: () => {
            setEditOpen(false);
            setEditingOption(null);
          },
          onSuccess: () => {
            setEditOpen(false);
            setEditingOption(null);
            fetchOptions();
          },
          option: editingOption,
        })}
      </>
    );
  }